import { useEffect, useMemo, useState } from "react";
import { useSearchConfig } from "../../../context/SearchConfigContext";
import { AppliedFilterInfo, PersonInfo, SortDirection } from "../../../models/person.model";
import { PersonService } from "../../../services/personService";
import { parseKeywords } from "../../../utils/keywordUtils";

interface UseCVListDataProps {
    searchQuery: string;
    debouncedSearchQuery: string;
    pageSize: number;
    pageIndex: number;
    sortField: string | null;
    sortDirection: SortDirection;
    inactiveFilterActive: boolean;
    availabilityFilterActive: boolean;
    availabilityDays: number | null;
    selectedLocation: string | null;
    selectedLanguage: number | null;
    selectedProficiency: number | null;
}

export function useCVListData({
    searchQuery,
    debouncedSearchQuery,
    pageSize,
    pageIndex,
    sortField,
    sortDirection,
    inactiveFilterActive,
    availabilityFilterActive,
    availabilityDays,
    selectedLocation,
    selectedLanguage,
    selectedProficiency,
}: UseCVListDataProps) {
    const personService = useMemo(() => new PersonService(), []);
    const { config: searchConfig } = useSearchConfig();

    const [persons, setPersons] = useState<PersonInfo[]>([]);
    const [loading, setLoading] = useState(true);
    const [totalItems, setTotalItems] = useState(0);
    const [totalPages, setTotalPages] = useState(1);
    const [searchError, setSearchError] = useState<string | null>(null);
    const [searchStartTime, setSearchStartTime] = useState<Date | null>(null);
    const [searchDuration, setSearchDuration] = useState(0);
    const [appliedFilters, setAppliedFilters] = useState<AppliedFilterInfo | null>(null);

    const currentKeywords = useMemo(
        () => parseKeywords(debouncedSearchQuery),
        [debouncedSearchQuery]
    );

    // Timer effect for search duration
    useEffect(() => {
        let intervalId: NodeJS.Timeout;

        if (searchStartTime && loading) {
            intervalId = setInterval(() => {
                setSearchDuration(
                    Math.round((new Date().getTime() - searchStartTime.getTime()) / 1000)
                );
            }, 1000);
        } else {
            setSearchDuration(0);
        }

        return () => {
            if (intervalId) {
                clearInterval(intervalId);
            }
        };
    }, [searchStartTime, loading]);

    // Reusable function to load persons with consistent parameters
    const loadPersonsWithCurrentParams = async (
        overrideKeywords?: string[],
        overridePageIndex?: number,
        overrideSortField?: string | null,
        overrideSortDirection?: SortDirection,
        overrideInactive?: boolean,
        overrideAvailability?: boolean,
        overrideLocation?: string,
        options?: any
    ) => {
        const keywords = overrideKeywords ?? currentKeywords;
        const effectiveSortField = (overrideSortField ?? sortField) || "";

        return await personService.getPersonsByPageAndFilter(
            pageSize,
            overridePageIndex ?? pageIndex,
            effectiveSortField,
            overrideSortDirection ?? sortDirection,
            keywords,
            overrideInactive ?? inactiveFilterActive,
            (overrideAvailability ?? availabilityFilterActive) ? 0 : availabilityDays,
            (overrideLocation ?? selectedLocation) || undefined,
            searchConfig.matchTechThreshold,
            searchConfig.matchCertThreshold,
            searchConfig.maxTechMatches,
            searchConfig.maxCertMatches,
            searchConfig.exactMatchOnly,
            searchConfig.exactTechMatchOnly,
            searchConfig.exactCertMatchOnly,
            searchConfig.useTechSearch,
            searchConfig.useCertSearch,
            searchConfig.filteringOperator,
            selectedLanguage ?? undefined,
            selectedProficiency ?? undefined,
            options
        );
    };

    // Load persons data
    useEffect(() => {
        const abortController = new AbortController();

        const loadPersons = async () => {
            // Yield to the macrotask queue so React StrictMode's cleanup (which calls
            // abortController.abort()) can run before we start any network request.
            // This prevents the double-call: cleanup fires after the yield but before
            // the signal check, so the throwaway first mount never reaches the backend.
            await new Promise<void>(resolve => setTimeout(resolve, 0));

            if (abortController.signal.aborted) {
                return;
            }

            try {
                setLoading(true);
                setSearchError(null);
                setSearchStartTime(new Date());

                const page = await loadPersonsWithCurrentParams(
                    undefined,
                    undefined,
                    undefined,
                    undefined,
                    undefined,
                    undefined,
                    undefined,
                    { signal: abortController.signal }
                );

                if (!abortController.signal.aborted) {
                    if (Array.isArray(page.page)) {
                        setPersons(page.page);
                        setTotalItems(page.allDataCount);
                        setTotalPages(Math.ceil(page.allDataCount / pageSize));
                        setAppliedFilters(page.appliedFilters || null);
                        setSearchError(null);
                    } else {
                        setSearchError("Invalid data format received from server");
                    }
                }
            } catch (error: any) {
                if (!abortController.signal.aborted) {
                    if (error.name === "AbortError") {
                        // Intentional abort from a newer effect run — ignore silently.
                    } else if (error.message && error.message.includes("timed out")) {
                        setSearchError(error.message);
                    } else {
                        setSearchError(
                            "An error occurred while searching. Please try again with more specific search terms."
                        );
                    }
                }
            } finally {
                if (!abortController.signal.aborted) {
                    setLoading(false);
                    setSearchStartTime(null);
                }
            }
        };

        void loadPersons();

        return () => {
            abortController.abort();
        };
    }, [
        pageSize,
        pageIndex,
        sortField,
        sortDirection,
        debouncedSearchQuery,
        inactiveFilterActive,
        availabilityFilterActive,
        availabilityDays,
        selectedLocation,
        selectedLanguage,
        selectedProficiency,
        searchConfig,
    ]);

    const refetchData = async () => {
        try {
            const keywords = parseKeywords(searchQuery);

            const page = await personService.getPersonsByPageAndFilter(
                pageSize,
                pageIndex,
                sortField || "",
                sortDirection,
                keywords,
                inactiveFilterActive,
                availabilityFilterActive ? 0 : availabilityDays,
                selectedLocation || undefined,
                searchConfig.matchTechThreshold,
                searchConfig.matchCertThreshold,
                searchConfig.maxTechMatches,
                searchConfig.maxCertMatches,
                searchConfig.exactMatchOnly,
                searchConfig.exactTechMatchOnly,
                searchConfig.exactCertMatchOnly,
                searchConfig.useTechSearch,
                searchConfig.useCertSearch,
                searchConfig.filteringOperator,
                selectedLanguage ?? undefined,
                selectedProficiency ?? undefined
            );

            setPersons(page.page);
            setTotalItems(page.allDataCount);
            setTotalPages(Math.ceil(page.allDataCount / pageSize));
        } catch (error) {
            console.error("Error fetching persons:", error);
        }
    };

    return {
        persons,
        loading,
        totalItems,
        totalPages,
        searchError,
        searchStartTime,
        searchDuration,
        appliedFilters,
        currentKeywords,
        refetchData,
        setSearchError,
        setLoading,
        setSearchStartTime,
        setPersons,
        setTotalItems,
        setTotalPages,
        loadPersonsWithCurrentParams,
    };
}

